import type { ReactNode } from "react";
import {
  Boxes,
  Calculator,
  LayoutDashboard,
  Settings,
  ShoppingCart,
  Users,
  Wallet,
  Wrench,
  type LucideIcon,
} from "lucide-react";
import { can, type Action, type Module, type Permissions } from "@cx/db";

export type RenderLink = (to: string, children: ReactNode, active: boolean, className?: string) => ReactNode;
/** `anyOf`: basta uma das permissões (ex.: importação, que serve a quem cria em qualquer um dos módulos). */
export type MenuItem = { label: string; to: string; perm?: [Module, Action]; anyOf?: [Module, Action][] };
/** `icon` é o componente: import nomeado mantém o tree-shaking do lucide (FE13). */
export type MenuGroup = { key: string; label: string; icon: LucideIcon; items: MenuItem[] };

/** Ordem e nomes do Bling (PRD). Itens sem rota ainda ficam fora até a fase que os entrega. */
export const MENU: MenuGroup[] = [
  {
    key: "meu-negocio",
    label: "Meu Negócio",
    icon: LayoutDashboard,
    items: [
      { label: "Dashboard", to: "/meu-negocio/dashboard", perm: ["dashboard", "read"] },
      { label: "Diário", to: "/meu-negocio/diario", perm: ["dashboard", "read"] },
      { label: "Pedidos de venda", to: "/meu-negocio/pedidos", perm: ["dashboard", "read"] },
      { label: "Vendedores e canais", to: "/meu-negocio/vendedores-canais", perm: ["dashboard", "read"] },
      { label: "Curva ABC", to: "/meu-negocio/curva-abc", perm: ["dashboard", "read"] },
      { label: "Clientes", to: "/meu-negocio/clientes", perm: ["dashboard", "read"] },
      { label: "Produtos e estoque", to: "/meu-negocio/produtos", perm: ["dashboard", "read"] },
      { label: "Consolidado", to: "/meu-negocio/consolidado", perm: ["dashboard", "read"] },
      { label: "Metas", to: "/meu-negocio/metas", perm: ["goals", "read"] },
      { label: "WhatsApp", to: "/meu-negocio/whatsapp", perm: ["copilot", "read"] },
      { label: "Relatórios", to: "/meu-negocio/relatorios", perm: ["custom_reports", "read"] },
      { label: "Agentes de IA", to: "/meu-negocio/agentes", perm: ["ai_agents", "read"] },
    ],
  },
  {
    key: "cadastros",
    label: "Cadastros",
    icon: Users,
    items: [
      { label: "Clientes e Fornecedores", to: "/cadastros/contatos", perm: ["contacts", "read"] },
      { label: "Produtos", to: "/cadastros/produtos", perm: ["products", "read"] },
      { label: "Cadastro inteligente", to: "/cadastros/cadastro-inteligente", perm: ["products", "create"] },
      { label: "Listas de preços", to: "/cadastros/listas-de-precos", perm: ["price_lists", "read"] },
      {
        label: "Condições de pagamento",
        to: "/cadastros/condicoes-de-pagamento",
        perm: ["payment_terms", "read"],
      },
      { label: "Vendedores", to: "/cadastros/vendedores", perm: ["sellers", "read"] },
      { label: "Etiquetas", to: "/cadastros/etiquetas", perm: ["label_templates", "read"] },
      {
        label: "Categorias financeiras",
        to: "/cadastros/categorias-financeiras",
        perm: ["financial_categories", "read"],
      },
      {
        label: "Formas de pagamento",
        to: "/cadastros/formas-de-pagamento",
        perm: ["payment_methods", "read"],
      },
    ],
  },
  {
    key: "suprimentos",
    label: "Suprimentos",
    icon: Boxes,
    items: [
      { label: "Controle de estoque", to: "/suprimentos/estoque", perm: ["stock", "read"] },
      { label: "Lotes", to: "/suprimentos/lotes", perm: ["stock", "read"] },
      { label: "Conferência de estoque", to: "/suprimentos/conferencias", perm: ["stock", "read"] },
      { label: "Depósitos", to: "/suprimentos/depositos", perm: ["warehouses", "read"] },
      { label: "Pedidos de compra", to: "/suprimentos/pedidos-de-compra", perm: ["purchase_orders", "read"] },
      {
        label: "Ordens de produção",
        to: "/suprimentos/ordens-de-producao",
        perm: ["production_orders", "read"],
      },
      { label: "Notas de entrada", to: "/suprimentos/notas-de-entrada", perm: ["inbound_invoices", "read"] },
    ],
  },
  {
    key: "vendas",
    label: "Vendas",
    icon: ShoppingCart,
    items: [
      { label: "Propostas comerciais", to: "/vendas/propostas", perm: ["quotes", "read"] },
      { label: "Pedidos de venda", to: "/vendas/pedidos", perm: ["sales_orders", "read"] },
      { label: "PDV (frente de caixa)", to: "/pdv", perm: ["pos", "create"] },
      { label: "Vendas do PDV", to: "/vendas/pdv", perm: ["pos", "read"] },
      { label: "Controle de caixa", to: "/vendas/pdv/caixas", perm: ["pos", "read"] },
      { label: "Notas fiscais", to: "/vendas/notas-fiscais", perm: ["invoices", "read"] },
      { label: "Guias GNRE/DARE", to: "/vendas/gnre", perm: ["gnre", "read"] },
      { label: "Logística (envios)", to: "/vendas/logistica", perm: ["logistics", "read"] },
      { label: "Remessas de postagem", to: "/vendas/logistica/remessas", perm: ["logistics", "read"] },
      { label: "Checkout de pedidos", to: "/vendas/checkout", perm: ["checkout", "read"] },
      { label: "Comissões", to: "/vendas/comissoes", perm: ["sellers", "read"] },
      {
        label: "Anúncios do marketplace",
        to: "/vendas/marketplace/anuncios",
        perm: ["marketplaces", "read"],
      },
      { label: "Pedidos do marketplace", to: "/vendas/marketplace/pedidos", perm: ["marketplaces", "read"] },
    ],
  },
  {
    key: "financas",
    label: "Finanças",
    icon: Wallet,
    items: [
      { label: "Contas a receber", to: "/financas/contas-a-receber", perm: ["receivables", "read"] },
      { label: "Contas a pagar", to: "/financas/contas-a-pagar", perm: ["payables", "read"] },
      // T2.7: pedidos atendidos do mesmo cliente numa NF-e e/ou num conjunto de contas a receber.
      {
        label: "Faturamento agrupado",
        to: "/financas/faturamento-agrupado",
        perm: ["grouped_billing", "read"],
      },
      { label: "Caixas e bancos", to: "/financas/caixas-e-bancos", perm: ["financial_accounts", "read"] },
      { label: "Transferências", to: "/financas/transferencias", perm: ["financial_accounts", "read"] },
      // T2.10: boletos da carteira própria (CNAB 240): remessa e retorno.
      { label: "Remessas e retornos", to: "/financas/remessas-e-retornos", perm: ["cnab", "read"] },
    ],
  },
  {
    key: "servicos",
    label: "Serviços",
    icon: Wrench,
    // T2.8: contratos com cobrança recorrente (contas a receber + Pix) e NFS-e. T2.9: ordens de serviço.
    items: [
      { label: "Ordens de serviço", to: "/servicos/ordens-de-servico", perm: ["service_orders", "read"] },
      { label: "Contratos", to: "/servicos/contratos", perm: ["contracts", "read"] },
      { label: "Cobranças de contratos", to: "/servicos/cobrancas", perm: ["contracts", "read"] },
      { label: "Notas de serviço", to: "/servicos/notas-de-servico", perm: ["invoices", "read"] },
    ],
  },
  {
    key: "contabilidade",
    label: "Contabilidade",
    icon: Calculator,
    // T3.5: Espaço Meu Contador (pacote mensal), SPED Fiscal/Contribuições/Sintegra e DAS MEI.
    items: [
      { label: "Espaço Meu Contador", to: "/contabilidade/meu-contador", perm: ["accounting", "read"] },
      { label: "Arquivos fiscais", to: "/contabilidade/arquivos-fiscais", perm: ["accounting", "read"] },
      { label: "DAS MEI", to: "/contabilidade/das-mei", perm: ["accounting", "read"] },
    ],
  },
  {
    key: "preferencias",
    label: "Preferências",
    icon: Settings,
    items: [
      {
        label: "Campos customizados",
        to: "/preferencias/campos-customizados",
        perm: ["custom_fields", "update"],
      },
      { label: "Situações e transições", to: "/preferencias/situacoes", perm: ["statuses", "read"] },
      {
        label: "Certificado digital",
        to: "/preferencias/fiscal/certificado-digital",
        perm: ["fiscal_certificates", "read"],
      },
      {
        label: "Naturezas de operação",
        to: "/preferencias/fiscal/naturezas",
        perm: ["tax_operations", "read"],
      },
      {
        label: "Nota fiscal eletrônica",
        to: "/preferencias/fiscal/nfe",
        perm: ["invoices", "read"],
      },
      {
        label: "Nota fiscal de serviço",
        to: "/preferencias/fiscal/nfse",
        perm: ["invoices", "read"],
      },
      {
        label: "Integrações › Pix",
        to: "/preferencias/integracoes/pix",
        perm: ["payment_integrations", "read"],
      },
      {
        label: "Integrações › Mercado Livre",
        to: "/preferencias/integracoes/mercado-livre",
        perm: ["marketplaces", "read"],
      },
      {
        label: "Integrações › Shopee",
        to: "/preferencias/integracoes/shopee",
        perm: ["marketplaces", "read"],
      },
      {
        label: "Integrações › Canais de venda",
        to: "/preferencias/integracoes/canais",
        perm: ["marketplaces", "read"],
      },
      {
        label: "Integrações › Logística",
        to: "/preferencias/integracoes/logistica",
        perm: ["logistics", "read"],
      },
      {
        label: "Integrações › API e webhooks",
        to: "/preferencias/integracoes/api",
        perm: ["api_integrations", "read"],
      },
      {
        label: "Importação de planilhas",
        to: "/preferencias/importacoes",
        anyOf: [
          ["products", "create"],
          ["contacts", "create"],
          ["receivables", "create"],
          ["payables", "create"],
        ],
      },
      { label: "Consulta de exclusões", to: "/preferencias/exclusoes", perm: ["deletions", "read"] },
      { label: "Gestão de ações assíncronas", to: "/preferencias/acoes", perm: ["jobs", "read"] },
    ],
  },
];

export function filterMenu(menu: MenuGroup[], perms: Permissions): MenuGroup[] {
  return menu
    .map((g) => ({
      ...g,
      items: g.items.filter(
        (i) =>
          (!i.perm || can(perms, i.perm[0], i.perm[1])) &&
          (!i.anyOf || i.anyOf.some(([m, a]) => can(perms, m, a))),
      ),
    }))
    .filter((g) => g.items.length > 0);
}
